import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

// Route name mapping
const routeNames: Record<string, string> = {
  dashboard: 'Dashboard',
  applications: 'RTI Applications',
  analytics: 'Analytics',
  reports: 'Reports',
  profile: 'Profile',
  settings: 'Settings',
  notifications: 'Notifications',
  new: 'Create New',
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, className = '' }) => {
  const location = useLocation();

  // Generate breadcrumbs from current path if items not provided
  const breadcrumbItems: BreadcrumbItem[] = items || (() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) {
      return [{ label: 'Dashboard', href: '/dashboard' }];
    }

    return pathSegments.map((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const label = routeNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      
      return {
        label,
        href: index === pathSegments.length - 1 ? undefined : path,
      };
    });
  })();

  return (
    <nav className={`flex ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {/* Home Icon */}
        <li>
          <Link
            to="/dashboard"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <HomeIcon className="h-5 w-5" />
            <span className="sr-only">Home</span>
          </Link>
        </li>

        {breadcrumbItems.map((item, index) => (
          <li key={index} className="flex items-center">
            <ChevronRightIcon className="h-4 w-4 text-gray-400 mx-2" />
            
            {item.href ? (
              <Link
                to={item.href}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm font-medium text-gray-900 dark:text-white"
              >
                {item.label}
              </motion.span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

// Compact breadcrumbs for mobile
export const CompactBreadcrumbs: React.FC<BreadcrumbsProps> = ({ items, className = '' }) => {
  const location = useLocation();
  
  const breadcrumbItems: BreadcrumbItem[] = items || (() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    return pathSegments.map((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const label = routeNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      return {
        label,
        href: index === pathSegments.length - 1 ? undefined : path,
      };
    });
  })();

  const currentItem = breadcrumbItems[breadcrumbItems.length - 1];
  const previousItem = breadcrumbItems.length > 1 ? breadcrumbItems[breadcrumbItems.length - 2] : null;

  return (
    <nav className={`flex items-center ${className}`} aria-label="Breadcrumb">
      {previousItem && (
        <>
          <Link
            to={previousItem.href || '/dashboard'}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {previousItem.label}
          </Link>
          <ChevronRightIcon className="h-4 w-4 text-gray-400 mx-2" />
        </>
      )}
      <span className="text-sm font-medium text-gray-900 dark:text-white">
        {currentItem?.label || 'Dashboard'}
      </span>
    </nav>
  );
};
